import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Bold,
  Braces,
  Code,
  Image as ImageIcon,
  Italic,
  Link as LinkIcon,
  Loader2,
  PanelBottom,
  PanelTop,
  Quote,
  Sparkles,
  Strikethrough,
  Trash2,
  Underline,
  Wand2,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { DragScroll } from "@/components/DragScroll";
import { Button } from "@/design";
import { cn } from "@/lib/cn";
import { haptic } from "@/lib/telegram";
import { useEmojiAssets, type EmojiAsset } from "@/api/emoji";
import { useMailingSettings } from "@/api/mailing";
import {
  uploadTemplateMedia,
  useCreateTemplate,
  useSpintaxPreview,
  useUpdateTemplate,
  type MediaPosition,
  type MessageEntity,
  type Template,
  type TemplateKind,
} from "@/api/templates";

import { applyEntity, spliceText, toggleEntity } from "./entities";
import { HighlightedTextarea } from "./HighlightedTextarea";
import { ChatPreview } from "./ChatPreview";
import { PremiumEmojiPicker, type PickedEmoji } from "./PremiumEmojiPicker";

interface TemplateEditorSheetProps {
  open: boolean;
  onClose: () => void;
  template?: Template | null;
}

export function TemplateEditorSheet({ open, onClose, template }: TemplateEditorSheetProps) {
  const isEdit = !!template;

  const [name, setName] = useState("");
  const [kind, setKind] = useState<TemplateKind>("mail");
  const [text, setText] = useState("");
  const [entities, setEntities] = useState<MessageEntity[]>([]);
  const [mediaPath, setMediaPath] = useState<string | null>(null);
  const [mediaPosition, setMediaPosition] = useState<MediaPosition>("top");
  const [showEmoji, setShowEmoji] = useState(false);
  const [uploading, setUploading] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const selectionRef = useRef<{ start: number; end: number }>({ start: 0, end: 0 });

  const createMut = useCreateTemplate();
  const updateMut = useUpdateTemplate(template?.id ?? 0);
  const { data: mailingSettings } = useMailingSettings();

  const emojiAccountId =
    mailingSettings?.send_mode === "single"
      ? mailingSettings.single_account_id
      : null;
  const customEmojiIds = useMemo(
    () =>
      Array.from(
        new Set(
          entities
            .filter((e) => e.type === "custom_emoji" && e.custom_emoji_id)
            .map((e) => String(e.custom_emoji_id)),
        ),
      ),
    [entities],
  );
  const { data: emojiAssetData } = useEmojiAssets({
    accountId: emojiAccountId ?? undefined,
    documentIds: customEmojiIds,
    enabled: open && customEmojiIds.length > 0,
  });
  const emojiAssets = useMemo(() => {
    const out: Record<string, EmojiAsset> = {};
    for (const asset of emojiAssetData?.assets ?? []) {
      out[asset.document_id] = asset;
    }
    return out;
  }, [emojiAssetData]);

  // Reset on open
  useEffect(() => {
    if (!open) return;
    setName(template?.name ?? "");
    setKind(template?.kind ?? "mail");
    setText(template?.text ?? "");
    setEntities(template?.entities ?? []);
    setMediaPath(template?.media_path ?? null);
    setMediaPosition(template?.media_position ?? "top");
    setShowEmoji(false);
  }, [open, template]);

  const captureSelection = () => {
    const ta = textareaRef.current;
    if (!ta) return;
    selectionRef.current = { start: ta.selectionStart, end: ta.selectionEnd };
  };

  const applyTextChange = (
    newText: string,
    newEntities: MessageEntity[],
    cursor: number,
  ) => {
    setText(newText);
    setEntities(newEntities);
    requestAnimationFrame(() => {
      const ta = textareaRef.current;
      if (ta) {
        ta.focus();
        ta.setSelectionRange(cursor, cursor);
      }
    });
  };

  const handleFormat = (type: MessageEntity["type"]) => {
    captureSelection();
    const { start, end } = selectionRef.current;
    if (end <= start) {
      toast("Выделите текст для форматирования");
      return;
    }
    haptic.impact("light");
    setEntities((prev) => toggleEntity(prev, type, start, end - start));
  };

  const handleLink = () => {
    captureSelection();
    const { start, end } = selectionRef.current;
    if (end <= start) {
      toast("Выделите текст для ссылки");
      return;
    }
    const url = prompt("URL ссылки", "https://");
    if (!url || !url.trim()) return;
    haptic.impact("light");
    setEntities((prev) =>
      applyEntity(prev, { type: "text_link", offset: start, length: end - start, url: url.trim() }),
    );
  };

  const wrapSpintax = () => {
    captureSelection();
    const { start, end } = selectionRef.current;
    haptic.impact("light");
    const selected = text.slice(start, end);
    const insertion = selected ? `{${selected}|}` : "{вариант 1|вариант 2}";
    const next = spliceText(text, entities, start, end, insertion);
    const cursorPos = selected
      ? start + insertion.length - 1 // place caret right before the closing `}`
      : start + 1;
    applyTextChange(next.text, next.entities, cursorPos);
  };

  const insertEmoji = (e: PickedEmoji) => {
    captureSelection();
    const { start, end } = selectionRef.current;
    const result = spliceText(text, entities, start, end, e.unicode);
    const entityWithEmoji = applyEntity(result.entities, {
      type: "custom_emoji",
      offset: start,
      length: e.unicode.length,
      custom_emoji_id: e.document_id,
      custom_emoji_is_free: e.is_free,
    });
    applyTextChange(result.text, entityWithEmoji, start + e.unicode.length);
    setShowEmoji(false);
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newText = e.target.value;
    const oldText = text;
    if (newText === oldText) return;
    const ta = e.target;
    const newCursor = ta.selectionStart;
    let prefix = 0;
    while (prefix < oldText.length && prefix < newText.length && oldText[prefix] === newText[prefix]) {
      prefix++;
    }
    let suffix = 0;
    while (
      suffix < oldText.length - prefix &&
      suffix < newText.length - prefix &&
      oldText[oldText.length - 1 - suffix] === newText[newText.length - 1 - suffix]
    ) {
      suffix++;
    }
    const from = prefix;
    const to = oldText.length - suffix;
    const replacement = newText.slice(prefix, newText.length - suffix);
    const result = spliceText(oldText, entities, from, to, replacement);
    setText(result.text);
    setEntities(result.entities);
    selectionRef.current = { start: newCursor, end: newCursor };
  };

  const handleUploadMedia = async (file: File) => {
    setUploading(true);
    try {
      const res = await uploadTemplateMedia(file);
      setMediaPath(res.path);
      haptic.notify("success");
      toast.success("Медиа добавлено");
    } catch (err) {
      haptic.notify("error");
      toast.error(err instanceof Error ? err.message : "Не удалось загрузить медиа");
    } finally {
      setUploading(false);
    }
  };

  const canSave = name.trim().length > 0 && text.trim().length > 0;

  const handleSave = async () => {
    if (!canSave) return;
    haptic.impact("medium");
    const body = {
      kind,
      name: name.trim(),
      text,
      entities,
      media_path: mediaPath,
      media_position: mediaPosition,
    };
    try {
      if (isEdit && template) {
        await updateMut.mutateAsync(body);
        toast.success("Шаблон обновлён");
      } else {
        await createMut.mutateAsync(body);
        toast.success("Шаблон создан");
      }
      haptic.notify("success");
      onClose();
    } catch {
      haptic.notify("error");
      toast.error("Не удалось сохранить");
    }
  };

  const saving = createMut.isPending || updateMut.isPending;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
          />
          <motion.div
            className={cn(
              "fixed inset-x-0 bottom-0 top-8 z-50",
              "glass-strong rounded-t-[28px] overflow-hidden",
              "border-t border-white/[0.08] flex flex-col",
            )}
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 320, damping: 32 }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 pt-4 pb-3 shrink-0 border-b border-white/[0.04]">
              <button
                onClick={onClose}
                className="size-9 grid place-items-center rounded-full bg-white/[0.04] hover:bg-white/[0.08] transition-colors"
                aria-label="Закрыть"
              >
                <ArrowLeft className="size-4" />
              </button>
              <div className="text-base font-semibold">
                {isEdit ? "Шаблон" : "Новый шаблон"}
              </div>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={!canSave || saving}
                loading={saving}
              >
                Сохранить
              </Button>
            </div>

            {/* Scroll body */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
              {/* Name + kind */}
              <div className="space-y-2.5">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Название шаблона"
                  className={cn(
                    "w-full h-12 px-4 rounded-xl",
                    "bg-surface border border-white/[0.06]",
                    "text-base font-medium outline-none focus:border-accent/40 transition-colors",
                    "placeholder:text-text-faint",
                  )}
                />
                <div className="flex bg-white/[0.03] border border-white/[0.06] rounded-xl p-1">
                  <KindToggle
                    active={kind === "mail"}
                    onClick={() => setKind("mail")}
                    label="Рассылка"
                  />
                  <KindToggle
                    active={kind === "auto"}
                    onClick={() => setKind("auto")}
                    label="Автоответ"
                  />
                </div>
              </div>

              {/* Toolbar */}
              <Toolbar
                onBold={() => handleFormat("bold")}
                onItalic={() => handleFormat("italic")}
                onUnderline={() => handleFormat("underline")}
                onStrike={() => handleFormat("strikethrough")}
                onCode={() => handleFormat("code")}
                onSpoiler={() => handleFormat("spoiler")}
                onQuote={() => handleFormat("blockquote")}
                onLink={handleLink}
                onSpintax={wrapSpintax}
                onEmoji={() => setShowEmoji(true)}
              />

              {/* Inline-styled editor */}
              <HighlightedTextarea
                value={text}
                entities={entities}
                onChange={handleTextareaChange}
                onSelect={captureSelection}
                placeholder="Напишите сообщение... Используйте {вариант 1|вариант 2} для рандомизации."
                rows={10}
                textareaRef={textareaRef}
                emojiAssets={emojiAssets}
              />

              <div className="rounded-2xl border border-white/[0.06] bg-white/[0.025] px-3.5 py-3">
                <div className="flex items-start gap-2.5">
                  <div className="mt-0.5 size-7 shrink-0 rounded-lg bg-white/[0.04] text-accent grid place-items-center">
                    <Braces className="size-3.5" />
                  </div>
                  <div className="min-w-0 text-xs leading-relaxed text-text-muted">
                    <div className="font-medium text-text">Переменная гаранта</div>
                    <div className="mt-0.5">
                      Вставьте <span className="font-mono text-white">@{`{garant}`}</span> в любое место текста,
                      и при отправке подставится гарант конкретного чата.
                    </div>
                  </div>
                </div>
              </div>

              {/* Media */}
              <MediaSlot
                mediaPath={mediaPath}
                onPick={() => fileInputRef.current?.click()}
                onRemove={() => setMediaPath(null)}
                uploading={uploading}
              />
              {mediaPath && (
                <div className="flex items-center gap-2 rounded-2xl border border-white/[0.06] bg-white/[0.025] p-1">
                  <PositionToggle
                    active={mediaPosition === "top"}
                    onClick={() => setMediaPosition("top")}
                    icon={<PanelTop className="size-3.5" />}
                    label="Медиа сверху"
                  />
                  <PositionToggle
                    active={mediaPosition === "bottom"}
                    onClick={() => setMediaPosition("bottom")}
                    icon={<PanelBottom className="size-3.5" />}
                    label="Медиа снизу"
                  />
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleUploadMedia(f);
                  e.target.value = "";
                }}
              />

              {/* Chat-bubble preview */}
              <SectionHeader>Превью в Telegram</SectionHeader>
              <ChatPreview
                text={text}
                entities={entities}
                mediaPath={mediaPath}
                mediaPosition={mediaPosition}
                emojiAssets={emojiAssets}
              />

              {/* Spintax preview */}
              {hasSpintax(text) && (
                <>
                  <SectionHeader>Варианты Spintax</SectionHeader>
                  <SpintaxPreviewPanel text={text} />
                </>
              )}
            </div>
          </motion.div>

          <PremiumEmojiPicker
            open={showEmoji}
            onClose={() => setShowEmoji(false)}
            onPick={insertEmoji}
          />
        </>
      )}
    </AnimatePresence>
  );
}

// ============================================================
// Helpers / sub-components
// ============================================================

function hasSpintax(text: string): boolean {
  return /\{[^{}]*\|[^{}]*\}/.test(text);
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[10px] uppercase tracking-wider text-text-dim font-medium px-1 pt-1">
      {children}
    </div>
  );
}

function KindToggle({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      onClick={() => {
        haptic.select();
        onClick();
      }}
      className={cn(
        "relative flex-1 h-9 rounded-lg text-sm font-medium transition-colors",
        active ? "text-white" : "text-text-muted",
      )}
    >
      {active && (
        <motion.div
          layoutId="kind-pill"
          className="absolute inset-0 rounded-lg bg-gradient-accent"
          transition={{ type: "spring", stiffness: 360, damping: 32 }}
        />
      )}
      <span className="relative">{label}</span>
    </button>
  );
}

function PositionToggle({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      onClick={() => {
        haptic.select();
        onClick();
      }}
      className={cn(
        "flex-1 h-9 rounded-xl px-3 flex items-center justify-center gap-2 text-xs font-medium transition-colors",
        active ? "bg-accent text-white" : "text-text-muted hover:bg-white/[0.05]",
      )}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

function Toolbar({
  onBold,
  onItalic,
  onUnderline,
  onStrike,
  onCode,
  onSpoiler,
  onQuote,
  onLink,
  onSpintax,
  onEmoji,
}: {
  onBold: () => void;
  onItalic: () => void;
  onUnderline: () => void;
  onStrike: () => void;
  onCode: () => void;
  onSpoiler: () => void;
  onQuote: () => void;
  onLink: () => void;
  onSpintax: () => void;
  onEmoji: () => void;
}) {
  const tools = [
    { icon: Bold, action: onBold, label: "Жирный" },
    { icon: Italic, action: onItalic, label: "Курсив" },
    { icon: Underline, action: onUnderline, label: "Подчёркнутый" },
    { icon: Strikethrough, action: onStrike, label: "Зачёркнутый" },
    { icon: Code, action: onCode, label: "Код" },
    { icon: Wand2, action: onSpoiler, label: "Спойлер" },
    { icon: Quote, action: onQuote, label: "Цитата" },
    { icon: LinkIcon, action: onLink, label: "Ссылка" },
    { icon: Braces, action: onSpintax, label: "Spintax {a|b}" },
    { icon: Sparkles, action: onEmoji, label: "Премиум-эмодзи", accent: true },
  ];
  return (
    <DragScroll className="flex gap-1 -mx-1 px-1 py-1">
      {tools.map((t, i) => (
        <button
          key={i}
          onClick={t.action}
          title={t.label}
          className={cn(
            "size-10 shrink-0 rounded-xl grid place-items-center transition-all",
            "border border-white/[0.06] active:scale-95",
            t.accent
              ? "bg-accent-soft text-accent hover:bg-accent/20"
              : "bg-white/[0.03] text-text-muted hover:text-white hover:bg-white/[0.07]",
          )}
          aria-label={t.label}
        >
          <t.icon className="size-4" />
        </button>
      ))}
    </DragScroll>
  );
}

function MediaSlot({
  mediaPath,
  onPick,
  onRemove,
  uploading,
}: {
  mediaPath: string | null;
  onPick: () => void;
  onRemove: () => void;
  uploading: boolean;
}) {
  if (mediaPath) {
    return (
      <div className="glass rounded-2xl p-3 flex items-center gap-3">
        <div className="size-12 rounded-xl bg-accent-soft text-accent grid place-items-center shrink-0">
          <ImageIcon className="size-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">Медиа прикреплено</div>
          <div className="text-xs text-text-muted truncate">{mediaPath}</div>
        </div>
        <button
          onClick={() => {
            onRemove();
            haptic.impact("light");
          }}
          aria-label="Удалить медиа"
          className="size-9 grid place-items-center rounded-full bg-white/[0.04] hover:bg-danger/15 hover:text-danger transition-colors"
        >
          <Trash2 className="size-4" />
        </button>
      </div>
    );
  }
  return (
    <button
      onClick={onPick}
      disabled={uploading}
      className={cn(
        "w-full glass rounded-2xl p-4 flex items-center gap-3",
        "hover:bg-white/[0.05] transition-colors",
        "disabled:opacity-60",
      )}
    >
      <div className="size-10 rounded-xl bg-white/[0.04] text-text-muted grid place-items-center">
        {uploading ? <Loader2 className="size-4 animate-spin" /> : <ImageIcon className="size-4" />}
      </div>
      <div className="text-left">
        <div className="text-sm font-medium">
          {uploading ? "Загружаем..." : "Прикрепить медиа"}
        </div>
        <div className="text-xs text-text-muted">Фото или видео, до 100 МБ</div>
      </div>
    </button>
  );
}

function SpintaxPreviewPanel({ text }: { text: string }) {
  const previewMut = useSpintaxPreview();
  const [samples, setSamples] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    const handle = setTimeout(async () => {
      try {
        const res = await previewMut.mutateAsync({ text, samples: 3 });
        if (!cancelled) setSamples(res.samples);
      } catch {
        if (!cancelled) setSamples([]);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  return (
    <div className="rounded-2xl border border-accent/15 bg-accent-soft p-4 space-y-2.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Braces className="size-3.5 text-accent" />
          <div className="text-xs font-medium text-accent uppercase tracking-wider">
            3 случайных варианта
          </div>
        </div>
        <button
          onClick={async () => {
            haptic.select();
            const res = await previewMut.mutateAsync({ text, samples: 3 });
            setSamples(res.samples);
          }}
          className="text-[11px] text-text-muted hover:text-white transition-colors"
        >
          Обновить
        </button>
      </div>
      <div className="space-y-1.5">
        {(samples.length > 0 ? samples : ["—", "—", "—"]).map((s, i) => (
          <div
            key={i}
            className="text-xs text-text leading-relaxed bg-black/20 rounded-lg px-3 py-2"
          >
            {s}
          </div>
        ))}
      </div>
    </div>
  );
}
